import React, { useEffect, useMemo, useState } from 'react'
import { InputBase } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faSearch } from '@fortawesome/free-solid-svg-icons'
import { useGetProductsQuery } from '../../store/api/productsApi'
import { styled, alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { IProductGroup } from '../../types/app'
import { IProduct } from '../../types/products'
import './ProductsCostLayout.scss'
import { Outlet, useNavigate } from 'react-router-dom'

const c = 'products-cost-layout'

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 1),
  marginLeft: 0,
  width: '100%',
  boxShadow: '1px 0px 12px -5px gray',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}))

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '10ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}))

const ProductsCostLayout = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [product, setProduct] = useState<IProduct>()
  const [totalPrice, setTotalPrice] = useState({ id: '', total_cost: '' })
  const { data: products = [] } = useGetProductsQuery()
  const [searchValue, setSearchValue] = useState('')

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchValue(e.target.value)

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const filteredProducts = useMemo(() => {
    if (searchValue === '') {
      return products
    }

    return products.map((product: IProductGroup) => {
      const updatedItems = product.items?.filter(
        (item: IProduct) =>
          (item.name &&
            item.name?.toLowerCase().includes(searchValue.toLowerCase())) ||
          (item.ru_name &&
            item.ru_name?.toLowerCase().includes(searchValue.toLowerCase())),
      )

      return {
        ...product,
        items: updatedItems,
      }
    })
  }, [searchValue, products])

  function handleEdit(item: IProduct) {
    setProduct(item)
    navigate(item._id)
  }

  return (
    <div className={c}>
      <div className={`${c}__container`}>
        <div className={`${c}__container__page-header`}>
          <div className={`${c}__container__page-header-title`}>
            Costuri Produse
          </div>
        </div>
        <div className={`${c}__container__header`}>
          <div className={`${c}__container__header-search`}>
            <Search>
              <SearchIconWrapper>
                <FontAwesomeIcon icon={faSearch} />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder={t('SEARCH')}
                value={searchValue}
                onChange={handleChange}
                inputProps={{ 'aria-label': 'search' }}
              />
            </Search>
          </div>
        </div>
        <div className={`${c}__container__products-cost`}>
          {!!filteredProducts.length &&
            filteredProducts?.map((product) => {
              if (!product.items.length) {
                return null
              }

              return (
                <div
                  key={product.category_id}
                  className={`${c}__container__products-cost-container`}
                >
                  <div
                    className={`${c}__container__products-cost-container-category`}
                  >
                    {product.category_name}
                  </div>
                  <div
                    className={`${c}__container__products-cost-container-items`}
                  >
                    {product.items.map((item: IProduct) => {
                      return (
                        <div
                          key={item._id}
                          className={`${c}__container__products-cost-container-items-item`}
                        >
                          <div
                            className={`${c}__container__products-cost-container-items-item-title`}
                          >
                            {item.name}
                          </div>

                          <div
                            className={`${c}__container__products-cost-container-items-item-actions`}
                          >
                            <div
                              className={`${c}__container__products-cost-container-items-item-cost`}
                            >
                              {totalPrice?.id == item._id
                                ? `${Number(totalPrice?.total_cost).toFixed(2)} lei`
                                : item.total_cost
                                  ? `${Number(item.total_cost).toFixed(2)} lei`
                                  : '-'}
                            </div>
                            <button
                              className={`${c}__container__products-cost-container-items-item-actions-btn`}
                              onClick={() => handleEdit(item)}
                              title='Editează'
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
        </div>
      </div>
      <Outlet context={{ products, setTotalPrice }} />
    </div>
  )
}

export default ProductsCostLayout
